import React from 'react'
import { Dejargonator, TextBody } from '@ally/metronome-ui'
import styled from 'styled-components'
import {
  Dd,
  Dt,
  StyledDejargTrigger,
} from '../common/DlComponents/StyledDlElements'
import { getDaysDifference, roundTwoDecimals } from '../../utils/formatter'
import { useAccountDetailsContext } from '../../services/AccountDetailsProvider'

// style override for common Dd
const TopCardDdWrapper = styled.span`
  > span {
    margin-inline-start: 0;
  }
`

const gracePeriodDejarg =
  "Because your CD matured, your new APY will be available the day after your grace period ends and is based on your term and balance tier at that time. Keep in mind, your CD includes the Ally Ten Day Best Rate Guarantee, so you'll get the best rate we offer for your term and balance tier if our rate goes up within 10 days of renewal."
const nonGracePeriodDejarg =
  "When you fund your CD within 10 days of your open date, you'll get the best rate we offer for your term and balance tier if our rate goes up within that time. The Ally Ten Day Best Rate Guarantee also applies at renewal. The rate on your account won't be updated until the end of the Ally Ten Day Best Rate Guarantee period, or the funding date (whichever is later)."

const DAY_LIMIT_FOR_BEST_RATE_GUARANTEE = 10

const APYHeading: React.FC = () => (
  <TextBody tag="span" size="xs" weight="bold" data-testid="apy-heading">
    ANNUAL PERCENTAGE YIELD
  </TextBody>
)

export const APY: React.FC = () => {
  const { accountDetails } = useAccountDetailsContext()
  if (accountDetails) {
    const differenceInDays = getDaysDifference(accountDetails.acctOpenDate)
    const showAPYDejarg =
      accountDetails.inGracePeriod ||
      differenceInDays < DAY_LIMIT_FOR_BEST_RATE_GUARANTEE
    const accountClosed = accountDetails.accountStatus === 'Closed'
    const accountHasBalance = accountDetails.currentBalancePvtEncrypt > 0

    // Test when we launch CD Account Details
    /* istanbul ignore next */
    const blankOutApy =
      !(accountClosed && accountDetails.acctClosedDate) &&
      differenceInDays >= DAY_LIMIT_FOR_BEST_RATE_GUARANTEE &&
      !accountDetails.retirementAccountIndicator &&
      !accountHasBalance
    return (
      <>
        <Dt size="xs">
          {showAPYDejarg ? (
            <Dejargonator
              trigger={{
                variant: 'element',
                content: (
                  <StyledDejargTrigger>
                    <APYHeading />
                  </StyledDejargTrigger>
                ),
                ariaLabel: 'Annual Percentage Yield',
              }}
              body={
                accountDetails.inGracePeriod
                  ? gracePeriodDejarg
                  : nonGracePeriodDejarg
              }
              ariaLabelPopover={
                accountDetails.inGracePeriod
                  ? gracePeriodDejarg
                  : nonGracePeriodDejarg
              }
            />
          ) : (
            <APYHeading />
          )}
        </Dt>
        <TopCardDdWrapper>
          <Dd>
            {blankOutApy ? (
              <TextBody tag="span" size="lg">
                --
              </TextBody>
            ) : (
              <TextBody
                tag="span"
                size="lg"
                data-testid="annual-percentage-yield"
              >
                {roundTwoDecimals(accountDetails.apy)}%
              </TextBody>
            )}
          </Dd>
        </TopCardDdWrapper>
      </>
    )
  }
  return null
}
